<script lang="ts" setup>
import { ref, watch, getCurrentInstance } from "vue"
import { useRoute, useRouter } from "vue-router"
import Fuse from "fuse.js"

import { useProjects, fetchStudentPreviews } from "@/store/projects"
import {
  studentPagePreviewQuery,
  type KirbyStudentPreviewData,
} from "@/queries/student"
import ProjectGrid from "@/components/Projects/ProjectGrid.vue"

const route  = useRoute()
const router = useRouter()

const { visibleStudents, loadProjects } = useProjects()
await loadProjects()

const instance = getCurrentInstance()
const site     = instance?.appContext.config.globalProperties.$site

type Entry = {
  id: string
  uri: string
  name?: string
  title?: string
  programme?: string
  isGroup?: boolean
}

const studentEntries: Entry[] = visibleStudents.value.map(s => ({
  id:      s.id,
  uri:     s.uri,
  name:    s.name,
  title:   s.title,
  isGroup: s.isPartOfGroup === true,
}))

const programmeEntries: Entry[] = (site?.programmeGroups ?? []).flatMap(
  (group: any) =>
    group.courses.map((course: any) => ({
      id:        course.id,
      uri:       course.uri,
      programme: course.title,
    })),
)

const fuse = new Fuse<Entry>([...studentEntries, ...programmeEntries], {
  threshold:    0.3,
  keys: [
    { name: "name",      weight: 2   },
    { name: "title",     weight: 1.5 },
    { name: "programme", weight: 0.5 },
  ],
})

// ── Local input ───────────────────────────────────────────────────────────────
// Watch localQuery directly — don't drive search through the route.
// router.replace is async so multiple queued calls fire the route watcher
// multiple times, causing concurrent duplicate searches.
const localQuery = ref((route.query.q as string) ?? "")

const updateRoute = () => {
  router.replace({
    query: { ...route.query, q: localQuery.value.trim() || undefined },
  })
}

// ── Results ───────────────────────────────────────────────────────────────────
type ProgrammeEntry = Entry & { programme: string }

const programmeResults = ref<ProgrammeEntry[]>([])
const studentResults   = ref<KirbyStudentPreviewData[]>([])
const isSearching      = ref(false)
let searchSeq = 0

const runSearch = async (query: string) => {
  const trimmed = query.trim()

  if (!trimmed) {
    programmeResults.value = []
    studentResults.value   = []
    isSearching.value      = false
    return
  }

  const seq = ++searchSeq
  isSearching.value      = true
  // Clear immediately so stale results never linger
  studentResults.value   = []
  programmeResults.value = []

  const hits = fuse.search(trimmed)

  programmeResults.value = hits
    .filter(h => !!h.item.programme)
    .map(h => h.item as ProgrammeEntry)

  const seenUris  = new Set<string>()
  const uniqueIds: string[] = []
  hits
    .filter(h => h.item.name || h.item.title)
    .forEach(h => {
      if (!seenUris.has(h.item.uri)) {
        seenUris.add(h.item.uri)
        uniqueIds.push(h.item.id)
      }
    })

  const hydrated = await fetchStudentPreviews(uniqueIds, studentPagePreviewQuery)

  // Always reset isSearching
  isSearching.value = false

  // Discard stale response
  if (seq !== searchSeq) return

  const byId = new Map(hydrated.map(s => [s.id, s]))
  const seenHydratedUris = new Set<string>()
  studentResults.value = uniqueIds
    .map(id => byId.get(id))
    .filter((s): s is KirbyStudentPreviewData => {
      if (!s) return false
      if (seenHydratedUris.has(s.uri)) return false
      seenHydratedUris.add(s.uri)
      return true
    })
}

// Debounce: only run search after typing settles (150ms).
// This prevents rapid space-add/remove from firing multiple concurrent searches.
let debounceTimer: ReturnType<typeof setTimeout> | null = null
watch(localQuery, q => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => runSearch(q), 150)
}, { immediate: true })
</script>

<template>
  <div class="px-4 pb-12">
    <div class="pt-1 mb-6 rounded border">
      <input
        v-model="localQuery"
        class="inline-block w-full bg-transparent px-2 placeholder:text-current focus:outline-hidden placeholder:opacity-50 transition-opacity"
        placeholder="Search..."
        type="text"
        @input="updateRoute"
      />
    </div>

    <!-- Programmes -->
    <div v-if="programmeResults.length" class="mb-8">
      <div class="text-sm underline mb-linebreak">Programmes</div>
      <div class="flex flex-col gap-px">
        <RouterLink
          v-for="p in programmeResults"
          :key="p.id"
          :to="`/${p.uri}`"
          class="link"
          v-html="p.programme"
        />
      </div>
    </div>

    <!-- Students / projects -->
    <ProjectGrid v-if="studentResults.length" :students="studentResults">
      Projects
    </ProjectGrid>

    <!-- Empty state -->
    <div
      v-if="localQuery.trim() && !isSearching && !programmeResults.length && !studentResults.length"
      class="text-sm opacity-50"
    >
      No results for "{{ localQuery.trim() }}".
    </div>
  </div>
</template>
